<template>
  <div class="desktop-menu-container flex" :class="{ 'pointer-events-none opacity-60': isNavigating }">
    <!-- 最左侧图标栏 -->
    <el-aside width="50px" class="border-r border-gray-100 sidebar-left">
      <div 
        class="logo h-[50px] border-b border-gray-100 flex items-center justify-center cursor-pointer hover:bg-gray-50 transition-colors"
        title="切换侧边栏"
        @click="toggleSubMenu(!showSubMenu)"
      >
        <el-icon :size="24" :class="{ 'text-primary': showSubMenu }"><Grid /></el-icon>
      </div>
      <el-menu
        :default-active="currentMenu?.id"
        class="!border-r-0"
      >
        <el-menu-item 
          v-for="menu in menus" 
          :key="menu.id" 
          :index="menu.id" 
          class="!h-[50px] flex justify-center items-center relative menu-item-container"
          @mouseenter="handleMenuHover(menu, $event)"
          @mouseleave="handleMenuLeave"
          @click="(event) => handleMainMenuClick(menu.id, event)"
        >
          <div class="flex justify-center items-center w-full h-full">
            <iconpark-icon :name="menu.icon" :size="20"></iconpark-icon>
          </div>
        </el-menu-item>
      </el-menu>
    </el-aside>

    <!-- 收缩状态下的单一浮动二级菜单实例 -->
    <teleport to="body">
      <transition name="fade">
        <div 
          v-if="!showSubMenu && hoveredMenu && getSubMenus(hoveredMenu.id).length > 0"
          class="floating-submenu"
          :style="floatingMenuStyle"
          @mouseenter="keepFloatingMenu(hoveredMenu)"
          @mouseleave="handleMenuLeave"
        >
          <div class="floating-submenu-header">{{ hoveredMenu.name }}</div>
          <div 
            v-for="sub in getSubMenus(hoveredMenu.id)" 
            :key="sub.id"
            class="floating-submenu-item"
            :class="{ 'is-active': String(currentSubMenu?.id) === String(sub.id) }"
            @click.stop="onClickMenuItem(sub.id)"
          >
            <iconpark-icon v-if="sub.icon" :name="sub.icon" :size="16" class="mr-2" />
            <span>{{ sub.name }}</span>
          </div>
        </div>
      </transition>
    </teleport>

    <!-- 中间二级菜单栏 -->
    <el-aside v-if="showSubMenu" width="160px" class="border-r border-gray-100">
      <div class="h-[50px] border-b border-gray-100 flex items-center justify-between px-4">
        <span class="text-lg font-semibold">{{ currentMenu?.name }}</span>
        <el-button link @click="toggleSubMenu(false)">
          <template #icon><el-icon><Fold /></el-icon></template>
        </el-button>
      </div>
      <el-menu
        :default-active="currentSubMenu?.id"
        class="!border-r-0 sub-menu"
        @select="onClickMenuItem"
      >
        <el-menu-item v-for="menu in currentSubMenus" :key="menu.id" :index="menu.id" class="!h-[40px] my-[5px] justify-start! px-4!">
          <template #title>
            <div class="flex items-center">
              <iconpark-icon v-if="menu.icon" :name="menu.icon" :size="18" class="mr-1" />
              <span>{{ menu.name }}</span>
            </div>
          </template>
        </el-menu-item>
      </el-menu>
    </el-aside>
  </div>
</template>

<script setup>
import { router } from "@inertiajs/vue3"
import { Grid, Fold } from '@element-plus/icons-vue'

const SUBMENU_STORAGE_KEY = 'admin_submenu_visible'

const props = defineProps({
  currentMenu: {
    type: Object,
    default: () => ({})
  },
  currentSubMenu: {
    type: Object,
    default: () => ({})
  }
})

const emit = defineEmits(['update:currentMenu', 'update:currentSubMenu'])
const { menus, submenus } = useShareData()

const currentMenu = ref(props.currentMenu)
const currentSubMenus = computed(() => {
  if (!currentMenu.value?.id) return []
  return submenus.value.filter(menu => String(menu.parent) === String(currentMenu.value.id))
})
const currentSubMenu = ref(props.currentSubMenu)
const showSubMenu = ref(true)
const isNavigating = ref(false)
const hoveredMenu = ref(null)
const floatingMenuStyle = ref({ top: '0px', left: '50px', position: 'fixed', zIndex: 10000 })
let hoverTimer = null

// 监听 props 变化
watch(() => props.currentMenu, (val) => {
  currentMenu.value = val
}, { deep: true })

watch(() => props.currentSubMenu, (val) => {
  currentSubMenu.value = val
}, { deep: true })


// 从 localStorage 读取二级菜单展开状态
const loadSubMenuState = () => {
  const saved = localStorage.getItem(SUBMENU_STORAGE_KEY)
  if (saved !== null) showSubMenu.value = saved === 'true'
}

// 保存二级菜单展开状态到 localStorage
const toggleSubMenu = (visible) => {
  showSubMenu.value = visible
  localStorage.setItem(SUBMENU_STORAGE_KEY, String(visible))
}

// 获取指定菜单的子菜单
const getSubMenus = (parentId) => {
  if (!submenus.value || !Array.isArray(submenus.value)) return []
  return submenus.value.filter(menu => menu && String(menu.parent) === String(parentId))
}

// 处理菜单悬停（收缩状态下显示浮动菜单）
const handleMenuHover = (menu, event) => {
  if (showSubMenu.value || !menu) return
  
  clearTimeout(hoverTimer)
  hoveredMenu.value = menu
  
  if (event) {
    const rect = event.currentTarget.getBoundingClientRect()
    floatingMenuStyle.value = {
      top: `${rect.top}px`,
      left: '50px',
      position: 'fixed',
      zIndex: 10005,
      visibility: 'visible',
      display: 'block'
    }
  }
}

const keepFloatingMenu = (menu) => {
  clearTimeout(hoverTimer)
  hoveredMenu.value = menu
}

const handleMenuLeave = () => {
  hoverTimer = setTimeout(() => { 
    hoveredMenu.value = null 
  }, 300)
}

// 处理主菜单点击
const handleMainMenuClick = (id, event) => {
  const targetMenu = menus.value.find(menu => String(menu.id) === String(id))
  if (!targetMenu) return

  // 展开状态下点击当前已选菜单：切换收起
  if (showSubMenu.value && String(id) === String(currentMenu.value?.id)) {
    toggleSubMenu(false)
    return
  }
  
  // 切换一级菜单
  currentMenu.value = targetMenu
  emit('update:currentMenu', targetMenu)
  
  // 收起状态下：显示浮动菜单，不展开侧边栏
  if (!showSubMenu.value) {
    hoveredMenu.value = targetMenu
    if (event) {
      const rect = event.currentTarget.getBoundingClientRect()
      floatingMenuStyle.value = {
        top: `${rect.top}px`,
        left: '50px',
        position: 'fixed',
        zIndex: 10005,
        visibility: 'visible',
        display: 'block'
      }
    }
    clearTimeout(hoverTimer)
  } else {
    // 展开状态下：点击非当前菜单，保持展开并切换
    toggleSubMenu(true)
  }
}

// 处理子菜单点击
const onClickMenuItem = (id) => {
  const target = submenus.value.find(menu => String(menu.id) === String(id))
  if (target) {
    currentSubMenu.value = target
    emit('update:currentSubMenu', target)
    hoveredMenu.value = null
    // 确保在收起状态下点击后清除 hover 状态
    clearTimeout(hoverTimer)
    router.visit(target.path || '/admin')
  }
}

// 根据路由路径设置活动菜单
const setActiveMenuByPath = (path) => {
  if (!path || !submenus.value || submenus.value.length === 0) return

  // 1. 优先从路径匹配二级菜单
  const sub = submenus.value.find(s => s.path === path)
  if (sub) {
    currentSubMenu.value = sub
    // 2. 根据二级菜单找到一级菜单
    const parent = menus.value.find(m => String(m.id) === String(sub.parent))
    if (parent) {
      currentMenu.value = parent
      emit('update:currentMenu', parent)
    }
    emit('update:currentSubMenu', sub)
  } else {
    // 3. 如果没找到二级菜单，则根据路径匹配一级菜单
    const parent = menus.value.find(m => path.includes(String(m.id)))
    if (parent) {
      currentMenu.value = parent
      emit('update:currentMenu', parent)
      // 默认选中第一个子菜单
      const firstSub = getSubMenus(parent.id)[0]
      if (firstSub) {
        currentSubMenu.value = firstSub
        emit('update:currentSubMenu', firstSub)
      }
    }
  }
}

// 监听菜单数据变化，确保数据到达后正确设置激活状态
watch([() => menus.value, () => submenus.value], () => {
  setActiveMenuByPath(window?.location?.pathname)
}, { immediate: true })


// 监听路由事件，控制导航状态
router.on('start', () => { isNavigating.value = true })
router.on('finish', (event) => {
  isNavigating.value = false
  const path = event?.detail?.visit?.url?.pathname
  setActiveMenuByPath(path)
})

onMounted(() => {
  loadSubMenuState()
  setActiveMenuByPath(window?.location?.pathname)
})

onUnmounted(() => {
  clearTimeout(hoverTimer)
})
</script>

<style>
.desktop-menu-container {
  overflow: visible !important;
}

.sidebar-left {
  overflow: visible !important;
  z-index: 1001; /* 确保高于主体内容 */
}

.sidebar-left .el-aside__content,
.sidebar-left .el-scrollbar,
.sidebar-left .el-scrollbar__view,
.sidebar-left .el-scrollbar__wrap {
  overflow: visible !important;
}

.sidebar-left .el-menu {
  overflow: visible !important;
  border-right: none;
  background: transparent;
}

.el-menu-item {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0 !important;
  overflow: visible !important;
  position: relative;
}

.el-menu--horizontal>.el-menu-item.is-active {
  border-bottom: none;
}

.el-menu-item.is-active {
  background-color: var(--el-menu-hover-bg-color);
}

.floating-submenu {
  position: fixed;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  z-index: 10005;
  padding: 4px 0;
  min-width: 160px;
}

.floating-submenu-header {
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 4px;
}

.floating-submenu-item {
  display: flex;
  align-items: center;
  padding: 8px 16px;
  font-size: 14px;
  color: #606266;
  cursor: pointer;
  transition: all 0.2s;
}

.floating-submenu-item:hover {
  background-color: #f5f5f5;
  color: #722ED1;
}

.floating-submenu-item.is-active {
  background-color: #f5f0ff;
  color: #722ED1;
}

/* 过渡动画 */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style> 